import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { captureReviewTokenFromURL } from '../lib/api';

export default function ScrollToTop() {
  const { pathname, search, hash } = useLocation();
  useEffect(() => {
    // If the URL carries a hash (e.g. /courses/foo#modules), let the browser's
    // native fragment scrolling win — manually scrolling to 0 here would
    // override an anchor link on first paint and break in-page navigation.
    // We scroll the target into view ourselves because React mounts the
    // element AFTER initial HTML parse, so the browser's own scroll-to-hash
    // misses it. requestAnimationFrame defers until after the paint that
    // includes the freshly-mounted node.
    // If the URL carries ?review=<token>, stash it for subsequent API calls.
    // Cheap and idempotent — we re-check on every route change so a reviewer
    // can drop the param onto any path and have it picked up immediately.
    captureReviewTokenFromURL();
    if (hash) {
      // Try on the next frame (covers normal navigations) AND after a short
      // delay (covers slow first paints and headless browsers that grab the
      // viewport before requestAnimationFrame fires). Both attempts are
      // idempotent — once the element is in view, scrolling to it again is a
      // no-op. If the element never mounts, fall back to top.
      const id = hash.slice(1);
      const tryScroll = () => {
        const el = document.getElementById(id);
        if (el) {
          el.scrollIntoView({ behavior: 'auto', block: 'start' });
          return true;
        }
        return false;
      };
      requestAnimationFrame(() => { if (!tryScroll()) window.scrollTo(0, 0); });
      const t = setTimeout(tryScroll, 300);
      return () => clearTimeout(t);
    }
    window.scrollTo(0, 0);
    return undefined; // eslint-disable-line consistent-return
  }, [pathname, search, hash]);
  return null;
}
